import * as React from 'react';
import { useContext } from 'react';
import styled from 'styled-components';
import { GradientLayout } from '@/components/gradient-layout';
import { CloseEditorButton } from '@/features/editor/widgets/shared/inspector-widget-layout/close-editor-button';
import { BackEditorButton } from '@/features/editor/widgets/shared/inspector-widget-layout/back-editor-button';
import { VSpace } from '@/components/spacing';
import { TinyProgressBar } from '@/components/tiny-progress-bar';
import { DefaultTypography } from '@/components/typography';
import { useIsPreviewingDesktop } from '@/features/editor/shared/use-device-type';
import { EditorContext } from '@/features/editor/context/editor-context';
import { WidgetType } from '@/utils/definitions';
import { ExperienceApprovalStatus } from '@/webapi/use-experience-api';
import { PopConfirm } from '@/components/pop-confirm';

export interface InspectorWidgetLayoutProps {
  title: string | any;
  children?: any;
  footer?: any;
  footerJustify?: string;
  progress: number;

  hideBackButton?: boolean;
  backCaption?: string;
  onBackClicked?: () => void;

  showCloseButton?: boolean;

  progressWidthPercentDesk?: number;
  progressWidthPercentMobile?: number;
  stickyHeader?: string;
  stickyEnabled?: boolean;
}

export function InspectorWidgetLayout({
  title,
  children,
  footer,
  progress,
  hideBackButton,
  backCaption,
  onBackClicked,
  showCloseButton,
  progressWidthPercentDesk,
  progressWidthPercentMobile,
  footerJustify,
  stickyHeader,
  stickyEnabled,
}: InspectorWidgetLayoutProps) {
  const {
    inspectorNav: { currentWidget },
    experienceState: {
      currentExperience: { status, approvalStatus, isHidden },
      setHiddenState,
    },
    alignPreviewRight,
    isShown,
  } = useContext(EditorContext);
  const pendingChangesMessageShown =
    alignPreviewRight && isShown && currentWidget.kind === WidgetType.CHANGELOG;
  const isDesktop = useIsPreviewingDesktop();
  const pbWidth = isDesktop
    ? progressWidthPercentDesk || 60
    : progressWidthPercentMobile || 35;

  const shouldInlineBadge = [
    WidgetType.PAGE_REDIRECT,
    WidgetType.THEME_TEST,
    WidgetType.POST_PURCHASE_EDITOR,
    WidgetType.PRICE_TEST,
  ].includes(currentWidget.kind);

  return (
    <GradientLayout
      horizontalPadding={0.001}
      paddingBottom={1}
      overflowTop={{ heightInRem: 9, turnPointInPercent: 90 }}
      overflowBottom={{ heightInRem: 5, turnPointInPercent: 50 }}
      padding={pendingChangesMessageShown ? 7 : 5}
      headerTop={4}
      stickyEnabled={stickyEnabled}
      header={
        <InspectorHeaderWrapper>
          <span>
            {!hideBackButton && (
              <BackEditorButton caption={backCaption} onClick={onBackClicked} />
            )}
            {showCloseButton && <CloseEditorButton />}
          </span>
          <CustomizationName className={`${stickyEnabled ? `show` : `hide`}`}>
            {stickyHeader}
          </CustomizationName>
        </InspectorHeaderWrapper>
      }
      footer={footer}
      footerJustify={footerJustify}
      contentPadding={3}
    >
      <BadgesRow
        pendingChangesMessageShown={pendingChangesMessageShown}
        inline={shouldInlineBadge}
      >
        {status === `PUBLISHED` &&
          currentWidget.kind === WidgetType.CHANGELOG && (
            <PublishedBadge inline={shouldInlineBadge}>
              Published
            </PublishedBadge>
          )}
        {status !== `PUBLISHED` &&
          approvalStatus === ExperienceApprovalStatus.PENDING_APPROVAL && (
            <ApprovalBadge inline={shouldInlineBadge}>Pending</ApprovalBadge>
          )}
        {status !== `PUBLISHED` &&
          approvalStatus === ExperienceApprovalStatus.APPROVED_APPROVAL && (
            <ApprovalBadge inline={shouldInlineBadge}>Ready</ApprovalBadge>
          )}
        {isHidden && (
          <PopConfirm
            text="This experience is hidden. Would you like to make it visible?"
            onConfirm={async () => setHiddenState(false)}
          >
            <HiddenBadge inline={shouldInlineBadge}>Hidden</HiddenBadge>
          </PopConfirm>
        )}
      </BadgesRow>
      <InspectorTitleWrapper>{title}</InspectorTitleWrapper>
      <VSpace value={2} />
      {progress === 0 ? (
        <Separator width={pbWidth} />
      ) : (
        <TinyProgressBar
          initialValue={progress}
          heightInRem={0.4}
          widthInPercent={pbWidth}
        />
      )}
      <VSpace value={3} />
      {children}
      <VSpace value={2} />
    </GradientLayout>
  );
}

export const InspectorHeaderWrapper = styled.div`
  width: 100%;
  > span {
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    margin-left: -2rem;
    user-select: none;
  }
`;

const CustomizationName = styled.div`
  text-align: left;
  font-size: 2.2rem;
  &.hide {
    opacity: 0;
    visibility: hidden;
  }
  &.show {
    opacity: 1;
    visibility: visible;
  }

  transition: all 0.5s ease;
`;

const getBadgeTop = (
  inline?: boolean,
  pendingChangesMessageShown?: boolean,
) => {
  if (inline) {
    return `-1rem`;
  }
  return pendingChangesMessageShown ? `7rem` : `5rem`;
};

const BadgesRow = styled.div<{
  inline?: boolean;
  pendingChangesMessageShown?: boolean;
}>`
  position: ${(props) => (props?.inline ? `relative` : `absolute`)};
  top: ${(props) =>
    getBadgeTop(props?.inline, props?.pendingChangesMessageShown)};
  z-index: 29;
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const PublishedBadge = styled.span`
  font-size: 1.1rem;
  padding: 0.3rem 1rem;
  background: rgb(13, 231, 122);
  border-radius: 50px;
  color: white;
  font-weight: 600;
`;

const ApprovalBadge = styled(PublishedBadge)<{ inline?: boolean }>`
  && {
    background-color: #8abeff;
    position: ${(props) => (props?.inline ? `relative` : `absolute`)};
    z-index: 29;
  }
`;

const HiddenBadge = styled(PublishedBadge)`
  && {
    background-color: #97a0a8;
    cursor: pointer;
    &:hover {
      background-color: #7a8490;
    }
  }
`;

export const InspectorTitleWrapper = styled(DefaultTypography)`
  && {
    width: 100%;
    color: rgba(0, 0, 0, 0.85);
    font-family: Inter, serif;
    font-weight: 800;
    font-size: 2.5rem;
    letter-spacing: -0.11px;
    user-select: none;
  }
`;

const Separator = styled.div`
  height: 0.4rem;
  width: ${(props: { width: number }) => props.width}%;
  border-top: 0.1rem solid white;
  border-bottom: 0.1rem solid white;
  background: rgba(216, 216, 216, 0.48);
`;
